import { generateClient } from 'aws-amplify/data';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import awsConfig from '../../../amplify_outputs.json';

/**
 * @type {import('aws-amplify/data').Client<import('../../../amplify/data/resource').Schema>}
 */
const client = generateClient();

export const fetchSecrets = createAsyncThunk(
  'configuration/fetchSecrets',
  async (_, { rejectWithValue }) => {
    try {
      const response = await client.queries.fetchSecrets();

      if (!response.data || response.data.length === 0) {
        throw new Error('No configuration data returned from fetchSecrets.');
      }

      return JSON.parse(response.data);
    } catch (error) {
      console.error('Error in fetchSecrets:', error);
      return rejectWithValue(
        error instanceof Error ? error.message : 'An error occurred during secrets fetch.'
      );
    }
  }
);

export const setSecret = createAsyncThunk(
  'configuration/setSecret',
  async ({ field, value }, { rejectWithValue }) => {
    try {
      const response = await client.queries.setSecret({
        field,
        value,
      });

      if (!response.data || response.data.length === 0) {
        throw new Error('No configuration data returned from fetchSecrets.');
      }

      return JSON.parse(response.data);
    } catch (error) {
      console.error('Error in fetchSecrets:', error);
      return rejectWithValue(
        error instanceof Error ? error.message : 'An error occurred during secrets fetch.'
      );
    }
  }
);

export const fetchCompanyConfig = createAsyncThunk(
  'configuration/fetchCompanyConfig',
  async (_, { rejectWithValue }) => {
    try {
      const response = await client.models.CompanyConfig.list();

      const config = response.data[0];

      return config;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.message || 'An error occurred during google search.');
    }
  }
);

export const fetchIndividualConfig = createAsyncThunk(
  'configuration/fetchIndividualConfig',
  async (_, { rejectWithValue }) => {
    try {
      const response = await client.models.IndividualConfig.list();

      const config = response.data[0];

      return config;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.message || 'An error occurred during google search.');
    }
  }
);

export const fetchScreeningConfig = createAsyncThunk(
  'configuration/fetchScreeningConfig',
  async (_, { rejectWithValue }) => {
    try {
      const response = await client.models.ScreeningConfig.list();

      const config = response.data[0];

      return config;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.message || 'An error occurred during google search.');
    }
  }
);

export const updateCompanyConfig = createAsyncThunk(
  'configuration/updateCompanyConfig',
  async ({ field, subField, value }, { getState, rejectWithValue }) => {
    const state = getState();
    const { companyConfig } = state.configuration;

    try {
      const updatedConfig = {
        ...companyConfig,
        [field]: {
          ...companyConfig[field],
          [subField]: value,
        },
      };

      const { data: updatedCompanyConfig, errors } = await client.models.CompanyConfig.update({
        id: companyConfig.id,
        ...updatedConfig,
      });

      if (errors) {
        throw new Error(errors);
      }

      return updatedCompanyConfig;
    } catch (error) {
      console.error(error);
      return rejectWithValue(error.message || 'An error occurred while updating company config.');
    }
  }
);

export const googleSearch = createAsyncThunk(
  'configuration/googleSearch',
  async ({ query, field, maxItems }, { rejectWithValue }) => {
    try {
      const response = await client.queries.googleSearch({
        authMode: 'apiKey',
        authToken: awsConfig.data.api_key,
        query,
        maxItems,
      });

      const parsedData = JSON.parse(response.data);

      const parsedBody = JSON.parse(parsedData.body);

      return parsedBody;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.message || 'An error occurred during google search.');
    }
  }
);

export const yandexSearch = createAsyncThunk(
  'configuration/yandexSearch',
  async ({ query, field, maxItems }, { rejectWithValue }) => {
    try {
      const response = await client.queries.yandexSearch({
        authMode: 'apiKey',
        authToken: awsConfig.data.api_key,
        query,
        maxItems,
      });

      const parsedResponse = JSON.parse(response.data);

      return parsedResponse.body;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.message || 'An error occurred during google search.');
    }
  }
);

export const aiScraping = createAsyncThunk(
  'configuration/aiScraping',
  async ({ url, field, index, targetName, keywords }, { rejectWithValue }) => {
    try {
      const response = await client.queries.aiScraping({
        authMode: 'apiKey',
        authToken: awsConfig.data.api_key,
        url,
        targetName,
        keywords,
      });

      const jsonParsedData = JSON.parse(response.data);

      console.log(jsonParsedData);

      return jsonParsedData.body;
    } catch (error) {
      console.error('Error during scraping:', error.message || error);
      return rejectWithValue(error.message || 'An error occurred during scraping.');
    }
  }
);

export const aiGroupScraping = createAsyncThunk(
  'configuration/aiGroupScraping',
  async ({ urls, field, index, targetName, keywords }, { rejectWithValue }) => {
    try {
      const response = await client.queries.aiGroupScraping({
        authMode: 'apiKey',
        authToken: awsConfig.data.api_key,
        urls,
        targetName,
        keywords,
      });

      const jsonParsedData = JSON.parse(response.data);

      console.log(jsonParsedData);

      const results = JSON.parse(jsonParsedData.body);

      return results;
    } catch (error) {
      console.error('Error during scraping:', error.message || error);
      return rejectWithValue(error.message || 'An error occurred during scraping.');
    }
  }
);

export const riskRate = createAsyncThunk(
  'configuration/riskRate',
  async ({ findings, field, promptTemplate, language }, { rejectWithValue }) => {
    try {
      const response = await client.queries.riskRateAnalysis({
        authMode: 'apiKey',
        authToken: awsConfig.data.api_key,
        findings,
        promptTemplate,
        language,
      });

      if (response.data) {
        const parsedData = JSON.parse(response.data);

        if (parsedData && parsedData.body) {
          const bodyData = JSON.parse(parsedData.body);
          return bodyData.riskRate || {};
        }
      }

      throw new Error('Invalid response data format.');
    } catch (error) {
      return rejectWithValue(error.message || 'An error occurred during risk rate analysis.');
    }
  }
);

export const findingsAnalysis = createAsyncThunk(
  'configuration/findingsAnalysis',
  async (
    { article, field, promptTemplate, index, target, previousFindings },
    { rejectWithValue }
  ) => {
    console.log(target);
    try {
      const response = await client.queries.findingsAnalysis({
        authMode: 'apiKey',
        authToken: awsConfig.data.api_key,
        article,
        promptTemplate,
        target,
        previousFindings,
      });

      const parsedData = JSON.parse(response.data);

      const body = JSON.parse(parsedData.body);

      const { summary, criticality, title, reason } = body.content[0];
      return { summary, criticality, title, reason };
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.message || 'An error occurred during findings analysis.');
    }
  }
);

export const findingsGroupAnalysis = createAsyncThunk(
  'configuration/findingsGroupAnalysis',
  async (
    { articles, field, filterPromptTemplate, promptTemplate, target, entity, language },
    { rejectWithValue }
  ) => {
    try {
      const response = await client.queries.findingsGroupAnalysis({
        authMode: 'apiKey',
        authToken: awsConfig.data.api_key,
        articles,
        filterPromptTemplate,
        promptTemplate,
        target,
        language,
      });

      const parsedData = JSON.parse(response.data);
      const body = JSON.parse(parsedData.body);

      return body.content;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.message || 'An error occurred during findings analysis.');
    }
  }
);

export const dbSearch = createAsyncThunk(
  'configuration/dbSearch',
  async ({ companyName, selectPrompt, incorporation }, { rejectWithValue }) => {
    try {
      const response = await client.queries.dbSearch({
        authMode: 'apiKey',
        authToken: awsConfig.data.api_key,
        companyName,
        selectPrompt,
        incorporation,
      });

      const parsedData = JSON.parse(response.data);

      return parsedData.body;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.message || 'An error occurred during findings analysis.');
    }
  }
);

export const individualSearch = createAsyncThunk(
  'configuration/individualSearch',
  async ({ individualName }, { rejectWithValue }) => {
    try {
      const response = await client.queries.individualSearch({
        authMode: 'apiKey',
        authToken: awsConfig.data.api_key,
        individualName,
      });

      const parsedData = JSON.parse(response.data);

      return parsedData.body;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.message || 'An error occurred during findings analysis.');
    }
  }
);

const initialState = {
  loading: false,
  error: null,
  companyInput: {
    companyName: 'Select Company',
  },
  individualInput: {
    individualName: 'Select Individual',
  },
  screeningInput: {
    companyName: 'Select Company',
  },
  test: null,
  companyConfig: null,
  individualConfig: null,
  screeningConfig: null,
  secrets: null,
};

const configurationSlice = createSlice({
  name: 'configuration',
  initialState,
  reducers: {
    setCompanyInputField: (state, action) => {
      const { field, value } = action.payload;
      state.companyInput[field] = value;
    },
    setIndividualInputField: (state, action) => {
      const { field, value } = action.payload;
      state.individualInput[field] = value;
    },
    setScreeningInputField: (state, action) => {
      const { field, value } = action.payload;
      state.screeningInput[field] = value;
    },

    clearCompanyTest: (state) => {
      state.test = null;
    },
    setRiskRate: (state, action) => {
      const { field, riskRate: newRiskRate, reason } = action.payload;

      if (state.test[field]) {
        state.test[field].riskRate = {
          riskRate: newRiskRate,
          reason,
        };

        console.log(`Updated riskRate for field ${field}:`, state.test[field].riskRate);
      } else {
        console.log(`Field ${field} does not exist in state.test`);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSecrets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSecrets.fulfilled, (state, action) => {
        state.secrets = action.payload;
        state.loading = false;
      })
      .addCase(fetchSecrets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(googleSearch.pending, (state, action) => {
        const { field } = action.meta.arg;
        state[`searchLoading${field}`] = true;
        state.error = null;
      })
      .addCase(googleSearch.fulfilled, (state, action) => {
        const { field } = action.meta.arg;

        if (field) {
          if (!state.test) {
            state.test = {};
          }

          if (!state.test[field]) {
            state.test[field] = {};
          }

          const { results: googleQueries, cost } = action.payload;

          if (googleQueries && googleQueries.length > 0) {
            state.test[field].googleQueries = googleQueries;
          } else {
            console.log('No results found in the response');
          }

          if (!state.test[field].pricing) {
            state.test[field].pricing = {};
          }

          if (cost) {
            state.test[field].pricing.googleQueries = cost;
          } else {
            console.log('No cost found in the response');
          }
        }

        state[`searchLoading${field}`] = false;
      })

      .addCase(googleSearch.rejected, (state, action) => {
        const { field } = action.meta.arg;
        state[`searchLoading${field}`] = false;
        state.error = action.payload;
      })

      .addCase(yandexSearch.pending, (state, action) => {
        const { field } = action.meta.arg;
        state[`searchLoading${field}`] = true;
        state.error = null;
      })
      .addCase(yandexSearch.fulfilled, (state, action) => {
        const { field } = action.meta.arg;

        if (field) {
          if (!state.test) {
            state.test = {};
          }

          if (!state.test[field]) {
            state.test[field] = {};
          }

          const { yandexQueries, cost } = action.payload;

          if (yandexQueries && yandexQueries.length > 0) {
            state.test[field].yandexQueries = yandexQueries;
          } else {
            console.log('No results found in the response');
          }

          if (!state.test[field].pricing) {
            state.test[field].pricing = {};
          }

          if (cost) {
            state.test[field].pricing.yandexQueries = cost;
          } else {
            console.log('No cost found in the response');
          }
        }

        state[`searchLoading${field}`] = false;
      })

      .addCase(yandexSearch.rejected, (state, action) => {
        const { field } = action.meta.arg;
        state[`searchLoading${field}`] = true;
        state.error = action.payload;
      })

      .addCase(aiScraping.pending, (state, action) => {
        const { field } = action.meta.arg;
        state[`scrapingLoading${field}`] = true;
        state.error = null;
      })
      .addCase(aiScraping.fulfilled, (state, action) => {
        const { field, index } = action.meta.arg;

        if (field) {
          if (!state.test) {
            state.test = {};
          }

          if (!state.test[field]) {
            state.test[field] = {};
          }

          if (!Array.isArray(state.test[field].scraping)) {
            state.test[field].scraping = [];
          }

          state.test[field].scraping[index] = action.payload;
        }

        state[`scrapingLoading${field}`] = false;
      })

      .addCase(aiScraping.rejected, (state, action) => {
        const { field } = action.meta.arg;
        state[`scrapingLoading${field}`] = false;
        state.error = action.payload;
      })

      .addCase(aiGroupScraping.pending, (state, action) => {
        const { field } = action.meta.arg;
        state[`scrapingLoading${field}`] = true;
        state.error = null;
      })
      .addCase(aiGroupScraping.fulfilled, (state, action) => {
        const { field } = action.meta.arg;

        if (field) {
          if (!state.test) {
            state.test = {};
          }

          if (!state.test[field]) {
            state.test[field] = {};
          }

          if (!Array.isArray(state.test[field].scraping)) {
            state.test[field].scraping = [];
          }

          state.test[field].scraping = action.payload;
        }

        state[`scrapingLoading${field}`] = false;
      })

      .addCase(aiGroupScraping.rejected, (state, action) => {
        const { field } = action.meta.arg;
        state[`scrapingLoading${field}`] = false;
        state.error = action.payload;
      })

      .addCase(findingsAnalysis.pending, (state, action) => {
        const { field } = action.meta.arg;
        state[`analysisLoading${field}`] = true;
        state.error = null;
      })
      .addCase(findingsAnalysis.fulfilled, (state, action) => {
        const { field, index } = action.meta.arg;

        if (field) {
          if (!state.test) {
            state.test = {};
          }

          if (!state.test[field]) {
            state.test[field] = {};
          }

          if (!Array.isArray(state.test[field].analysis)) {
            state.test[field].analysis = [];
          }

          state.test[field].analysis[index] = action.payload;
        }

        state[`analysisLoading${field}`] = false;
      })

      .addCase(findingsAnalysis.rejected, (state, action) => {
        const { field } = action.meta.arg;
        state[`analysisLoading${field}`] = false;
        state.error = action.payload;
      })

      .addCase(findingsGroupAnalysis.pending, (state, action) => {
        const { field } = action.meta.arg;
        state[`analysisLoading${field}`] = true;
        state.error = null;
      })
      .addCase(findingsGroupAnalysis.fulfilled, (state, action) => {
        const { field, entity } = action.meta.arg;

        if (field) {
          if (!state.test) {
            state.test = {};
          }

          if (!state.test[field]) {
            state.test[field] = {};
          }

          if (!Array.isArray(state.test[field].analysis)) {
            state.test[field].analysis = [];
          }

          state.test[field].analysis = action.payload.map((item) => ({
            ...item,
            entity,
          }));

          state[`analysisLoading${field}`] = false;
        }
      })

      .addCase(findingsGroupAnalysis.rejected, (state, action) => {
        const { field } = action.meta.arg;
        state[`analysisLoading${field}`] = false;
        state.error = action.payload;
      })

      .addCase(fetchCompanyConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCompanyConfig.fulfilled, (state, action) => {
        state.companyConfig = action.payload;
        state.loading = false;
      })
      .addCase(fetchCompanyConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchIndividualConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchIndividualConfig.fulfilled, (state, action) => {
        state.individualConfig = action.payload;
        state.loading = false;
      })
      .addCase(fetchIndividualConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchScreeningConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchScreeningConfig.fulfilled, (state, action) => {
        state.screeningConfig = action.payload;
        state.loading = false;
      })
      .addCase(fetchScreeningConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateCompanyConfig.pending, (state, action) => {
        const { field, subField, value } = action.meta.arg;
        if (state.companyConfig && state.companyConfig[field]) {
          state.companyConfig[field][subField] = value;
        }
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCompanyConfig.fulfilled, (state, action) => {
        state.companyConfig = action.payload;
        state.loading = false;
      })
      .addCase(updateCompanyConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(dbSearch.pending, (state, action) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(dbSearch.fulfilled, (state, action) => {
        if (!state.test) {
          state.test = {};
        }

        if (!state.test.db) {
          state.test.db = {};
        }

        state.test.db = action.payload;

        state.loading = false;
      })

      .addCase(dbSearch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(individualSearch.pending, (state, action) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(individualSearch.fulfilled, (state, action) => {
        if (!state.test) {
          state.test = {};
        }

        if (!state.test.db) {
          state.test.individual = {};
        }

        state.test.individual = action.payload;

        state.loading = false;
      })

      .addCase(individualSearch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(riskRate.pending, (state, action) => {
        const { field } = action.meta.arg;
        state[`riskLoading${field}`] = true;
        state.error = null;
      })
      .addCase(riskRate.fulfilled, (state, action) => {
        const { field } = action.meta.arg;

        if (field) {
          if (!state.test) {
            state.test = {};
          }

          if (!state.test[field]) {
            state.test[field] = {};
          }

          if (
            typeof state.test[field].riskRate !== 'object' ||
            state.test[field].riskRate === null
          ) {
            state.test[field].riskRate = {};
          }

          state.test[field].riskRate = action.payload;
        }

        state[`riskLoading${field}`] = false;
      })

      .addCase(riskRate.rejected, (state, action) => {
        const { field } = action.meta.arg;
        state[`riskLoading${field}`] = false;
        state.error = action.payload;
      });
  },
});

export const {
  setCompanyInputField,
  setIndividualInputField,
  setScreeningInputField,
  clearCompanyTest,
  setRiskRate,
} = configurationSlice.actions;

export default configurationSlice.reducer;
